import React from 'react';
import { useNavigate } from 'react-router-dom';

import { LookoButton } from '@looko-design-system';
import { HStack, VStack } from '@looko-design-system';
import { ErrorOutlineOutlined } from '@mui/icons-material';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { Box, CircularProgress, Dialog, LinearProgress, Typography, colors } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { createQueryString } from '@toss/utils';
import { isAxiosError } from 'axios';

import { ACLOSET_NOTIFICATION_TYPE_SHORT_CODE_MAP } from '@/MetadataType';
import { useShopInfo2 } from '@/app/shop/shop-info/hooks';
import axiosInstance from '@/axios/axios';
import LoadingSpinner from '@/components/LoadingSpinner';
import useSalesFilterSearchParams from '@/features/filter/sales/useSalesFilterSearchParams';
import { claimMap } from '@/router/claimMap';
import { orderMap } from '@/router/orderMap';
import { productMap } from '@/router/productMap';
import { useShopAuth } from '@/state/userAuthContext';
import { acCaptureException } from '@/thirdparty/ASentry/Log';

import { useIntersect } from './useIntersection';
import { useNotificationDialog } from './useNotification';
import useNotificationData from './useNotificationData';

const intersectOption = {
  threshold: 0.8,
};

/**
 * AppBar 알림 Dialog
 */
export default function AppBarNotificationDialog() {
  const { isOpen, closeNotification } = useNotificationDialog();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { shopID } = useShopAuth();

  const { isDataLoading, notification, summary } = useNotificationData(isOpen);

  const { filterSearchParams, setFilterSearchParams } = useSalesFilterSearchParams();

  const ref = useIntersect<HTMLDivElement>(
    async (entry, observer) => {
      observer.unobserve(entry.target);

      if (notification.hasNextPage && !notification.isFetching) {
        notification.fetchNextPage();
      }
    },
    {
      threshold: 0.8,
    },
  );

  const handleDeleteNotification = async (notificationID: string) => {
    try {
      if (!shopID) {
        return;
      }

      await axiosInstance.delete(`/shops/${shopID}/notifications/${notificationID}`);

      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      });
    } catch (err) {
      if (isAxiosError(err)) {
        if (err.status === 404) {
          return;
        }
      }
      acCaptureException('알림 삭제 실패', err);
    }
  };

  const handleClickNavigate = React.useCallback(
    (notificationType: 'order' | 'sale' | 'platform' | undefined, notificationCode: string) => {
      switch (notificationType) {
        case 'order':
          switch (notificationCode) {
            case 'NOTIFICATION_ORDER-TYPE_CONFIRM-PAYMENT-ERROR': // 결제 확인 실패
              return navigate(`${orderMap['payment-pending'].path}`, { replace: true });

            case 'NOTIFICATION_ORDER-TYPE_CONFIRM-ERROR': // 주문 확인 실패 (배송준비중으로)
              return navigate(`${orderMap['payment-done'].path}`, { replace: true });

            case 'NOTIFICATION_ORDER-TYPE_CONFIRM-SHIPPING-ERROR': // 배송 확인 실패 (배송중으로)
            case 'NOTIFICATION_ORDER-TYPE_INVOICE-REQUEST-ERROR': // 운송장 번호 전송 실패
              return navigate(`${orderMap['shipping-pending'].path}`, { replace: true });

            case 'NOTIFICATION_ORDER-TYPE_APPROVE-CANCEL-ERROR': // 주문 취소 승인 실패
            case 'NOTIFICATION_ORDER-TYPE_CANCEL-ERROR': // 주문 취소 실패
            case 'NOTIFICATION_ORDER-TYPE_REJECT-CANCEL-ERROR': // 주문 취소 거부 실패
              return navigate(`${claimMap['order-cancel'].path}`, { replace: true });
          }
          break;

        case 'sale':
          return navigate(
            `${productMap['origin-list'].path}${createQueryString({
              ...filterSearchParams,
              saleOperation: 'SALE_OPERATION_FAILED',
            })}`,
          );

        default:
          return closeNotification();
      }
    },
    [closeNotification, filterSearchParams, navigate],
  );

  return (
    <Dialog
      open={isOpen}
      maxWidth={'lg'}
      sx={{ height: '560px' }}
      onClose={closeNotification}
      hideBackdrop
      PaperProps={{
        sx: {
          position: 'absolute',
          right: 80,
          top: 30,
        },
      }}
    >
      <VStack width={465}>
        {isDataLoading ? (
          <Box height={'100px'}>
            <LoadingSpinner />
          </Box>
        ) : summary.data.length < 1 && notification.data.length < 1 ? (
          <VStack height={'100px'} justifyContent={'center'} alignItems={'center'}>
            <Typography variant="Title_Looko">알림이 없습니다</Typography>
          </VStack>
        ) : (
          <>
            {summary.data.map(sum => {
              const progress = (sum.successCount / sum.totalCount) * 100;

              return (
                <HStack key={sum.saleTaskID} p={3} spacing={2} borderBottom={`1px solid ${colors.grey[300]}`}>
                  <VStack pt={0.5}>
                    <CircularProgress size={16} />
                  </VStack>

                  <VStack gap={2} width={'100%'}>
                    <Typography variant="Title_Looko">{sum.actionText} 처리 중입니다.</Typography>

                    <VStack width={'100%'}>
                      <Typography>
                        {sum.successCount}/{sum.totalCount}개 등록 완료
                      </Typography>
                      <VStack width={'100%'}>
                        <LinearProgress variant="determinate" value={progress} />
                      </VStack>
                    </VStack>

                    <Typography variant={'body2'} color={colors.grey[600]}>
                      {sum.createDate}
                    </Typography>
                  </VStack>
                </HStack>
              );
            })}

            {notification.data.map((noti, index) => (
              <HStack
                justifyContent={'space-between'}
                key={`${noti.notificationID} ${index}`}
                borderBottom={`1px solid ${colors.grey[300]}`}
                p={3}
              >
                {/* notificationType === platform은
                    현재는 연동 성공만 존재함. (연동 실패 알람은 존재하지만 서버에서 보내주지 않음)
               */}
                {noti.notificationType === 'platform' ? (
                  <HStack spacing={2}>
                    <VStack pt={0.2}>
                      {noti.notificationCode === ACLOSET_NOTIFICATION_TYPE_SHORT_CODE_MAP['delete-platfrom-success'] ? (
                        <CheckCircleOutlineOutlinedIcon color={'success'} fontSize="medium" />
                      ) : (
                        <ErrorOutlineOutlined color={'error'} fontSize="medium" />
                      )}
                    </VStack>
                    <VStack gap={2}>
                      <VStack spacing={0.5}>
                        <Typography variant="Title_Looko">{noti.actionText}</Typography>
                      </VStack>

                      <Typography variant="Label_Looko" color="text.secondary">
                        {noti.createDate}
                      </Typography>
                    </VStack>
                  </HStack>
                ) : (
                  <HStack spacing={2}>
                    <VStack pt={0.2}>
                      {noti.isFailure ? (
                        <ErrorOutlineOutlined color={'warning'} fontSize="medium" />
                      ) : (
                        <CheckCircleOutlineOutlinedIcon color={'success'} fontSize="medium" />
                      )}
                    </VStack>

                    <VStack gap={2}>
                      <VStack spacing={0.5}>
                        <HStack spacing={1}>
                          <Typography>
                            <Typography variant="Title_Looko">{noti.totalCount}개 상품</Typography>{' '}
                            <Typography variant="Title_Looko">{noti.actionText}</Typography>{' '}
                            <Typography variant="Title_Looko">
                              {noti.isFailure ? '처리 중 오류가 발생했습니다' : '완료되었습니다'}
                            </Typography>
                          </Typography>
                        </HStack>
                        {noti.isFailure && (
                          <HStack gap={0.5}>
                            <Typography variant="Body_Looko">성공:{noti.successCount}개</Typography>
                            <Typography variant="Body_Looko" color={theme => theme.palette.error.main}>
                              실패:{noti.failureCount}개
                            </Typography>
                          </HStack>
                        )}
                      </VStack>

                      <Typography variant="Label_Looko" color="text.secondary">
                        {noti.createDate}
                      </Typography>

                      {noti.isFailure && (
                        <HStack onClick={() => handleClickNavigate(noti.notificationType, noti.notificationCode)}>
                          <LookoButton size="small" widthSize="default" variant="outlined" color="inherit">
                            확인하기
                          </LookoButton>
                        </HStack>
                      )}
                    </VStack>
                  </HStack>
                )}

                <VStack>
                  <Typography
                    onClick={() => handleDeleteNotification(noti.notificationID)}
                    variant="Body_Looko"
                    color={theme => theme.palette.text.secondary}
                    sx={{
                      cursor: 'pointer',
                    }}
                  >
                    삭제
                  </Typography>
                </VStack>
              </HStack>
            ))}

            {notification.hasNextPage && notification.isFetching && <LoadingSpinner />}
            <div ref={ref} style={{ height: '1px', backgroundColor: 'red', width: '100%' }} />
          </>
        )}
      </VStack>
    </Dialog>
  );
}
